import { format } from "date-fns";
import { Committee, CommitteeMember, TermsOfReference } from "@/types/governance";
import { Badge } from "@/components/ui/badge";

interface TorPreviewDocumentProps {
  committee: Committee;
  tor: TermsOfReference;
  members: CommitteeMember[];
}

export function TorPreviewDocument({ committee, tor, members }: TorPreviewDocumentProps) {
  const memberById = new Map(members.map((member) => [member.id, member]));
  const approverNames = tor.approverIds.map((id) => memberById.get(id)?.name).filter(Boolean);
  const reviewDate = tor.nextReviewDate ? format(new Date(tor.nextReviewDate), "MMM d, yyyy") : "Not set";
  const approvalDate = tor.approvalDate ? format(new Date(tor.approvalDate), "MMM d, yyyy") : "Pending";

  return (
    <div className="mx-auto max-w-4xl space-y-6 rounded-lg border bg-white p-8 text-black shadow-sm">
      <div className="space-y-2 border-b pb-4">
        <h2 className="text-2xl font-semibold">Terms of Reference</h2>
        <p className="text-sm text-gray-600">{committee.name}</p>
        <Badge variant="outline">{tor.status.toUpperCase()}</Badge>
      </div>

      <section className="space-y-2">
        <h3 className="text-lg font-semibold">Document Governance & Version Control</h3>
        <div className="grid gap-2 text-sm md:grid-cols-2">
          <p><strong>Document Owner:</strong> {tor.documentOwner}</p>
          <p><strong>Contact:</strong> {tor.contactEmail}</p>
          <p><strong>Approval Authority:</strong> {tor.approvalAuthority}</p>
          <p><strong>Version:</strong> {tor.version}</p>
          <p><strong>Approval Date:</strong> {approvalDate}</p>
          <p><strong>Next Review Date:</strong> {reviewDate}</p>
        </div>
      </section>

      <section className="space-y-2">
        <h3 className="text-lg font-semibold">Approvals</h3>
        <p className="text-sm">Approvers: {approverNames.join(", ") || "None assigned"}</p>
        <div className="space-y-2">
          {tor.approvalLog.map((entry) => (
            <div key={entry.id} className="rounded border p-2 text-sm">
              <p className="font-medium">{memberById.get(entry.userId)?.name || "Unknown reviewer"}</p>
              <p className="text-gray-600">
                {entry.action} on {format(new Date(entry.createdAt), "MMM d, yyyy h:mm a")}
              </p>
              {entry.comment ? <p className="mt-1">{entry.comment}</p> : null}
            </div>
          ))}
          {tor.approvalLog.length === 0 && <p className="text-sm text-gray-600">No approvals yet.</p>}
        </div>
      </section>

      <section className="space-y-2">
        <h3 className="text-lg font-semibold">Version History</h3>
        <div className="space-y-2">
          {tor.versionHistory.map((history) => (
            <div key={history.id} className="rounded border p-2 text-sm">
              <p>
                <strong>{history.version}</strong> - {format(new Date(history.date), "MMM d, yyyy")}
              </p>
              <p className="text-gray-700">{history.comments}</p>
            </div>
          ))}
          {tor.versionHistory.length === 0 && <p className="text-sm text-gray-600">No version history entries.</p>}
        </div>
      </section>

      <section className="space-y-2">
        <h3 className="text-lg font-semibold">Purpose</h3>
        <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: tor.purpose }} />
      </section>

      <section className="space-y-2">
        <h3 className="text-lg font-semibold">Roles & Responsibilities</h3>
        <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: tor.rolesAndResponsibilities }} />
      </section>

      <section className="space-y-2">
        <h3 className="text-lg font-semibold">Agenda Content</h3>
        <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: tor.agendaContent }} />
      </section>
    </div>
  );
}
